import { type PermissionPath } from "../generated/permissionTypes.js";
import {
  allPermissions,
  rolePermissions,
} from "../generated/rolePermissions.js";
import { IdentityRole } from "../models/Identity.js";
import {
  apiKeyScopePermissions,
  apiKeyScopes,
  type ApiKeyScope,
} from "./apiKeyScopes.js";

const allApiKeyScopeCount = Object.keys(apiKeyScopes).length;

/**
 * Roles that always have full access to all blocks, regardless
 * of any additional fact permissions.
 */
export const rolesWithFullBlockAccess: IdentityRole[] = [
  IdentityRole.Executive,
  IdentityRole.Owner,
  IdentityRole.Bot,
];

/**
 * Roles for which block access is limited by default, unless explicitly
 * granted via fact permissions.
 */
export const rolesWithLimitedBlockAccess: IdentityRole[] = [
  IdentityRole.Marketing,
  IdentityRole.Sales,
  IdentityRole.Creator,
  IdentityRole.Advisor,
  IdentityRole.Guest,
];

export const teamMemberRoles: IdentityRole[] = [
  IdentityRole.Owner,
  IdentityRole.Executive,
  IdentityRole.Marketing,
  IdentityRole.Sales,
  IdentityRole.Creator,
];

export const permissionsByCategory: Record<string, PermissionPath[]> =
  allPermissions
    .filter((p) => p.showInUI)
    .reduce(
      (acc, permission) => {
        if (!acc[permission.category]) acc[permission.category] = [];
        acc[permission.category].push(permission.id);
        return acc;
      },
      {} as Record<string, PermissionPath[]>,
    );

/**
 * Checks whether a identity role has a specific permission.
 */
export function isGranted(props: {
  identityRole: IdentityRole;
  permission: PermissionPath;
}) {
  const { identityRole, permission } = props;
  const permissions = rolePermissions[identityRole]?.permissions ?? [];

  const allowedRoles = Object.keys(rolePermissions).filter((key) => {
    return rolePermissions[key as IdentityRole].permissions.includes(
      permission,
    );
  });
  return {
    hasPermission: permissions.includes(permission),
    allowedRoles,
  };
}

/**
 * The API key scopes an identity's role is allowed to grant to a personal
 * API key. Used to disable scope options in the UI when the user's role
 * doesn't cover them.
 */
export function getGrantableApiKeyScopes(
  identityRole: IdentityRole,
): ApiKeyScope[] {
  return (Object.keys(apiKeyScopePermissions) as ApiKeyScope[]).filter(
    (scope) => canGrantApiKeyScope(identityRole, scope),
  );
}

/**
 * Whether a role covers a single API key scope.
 */
export function canGrantApiKeyScope(
  identityRole: IdentityRole,
  scope: ApiKeyScope,
): boolean {
  const permission = apiKeyScopePermissions[scope];

  return (
    permission == null || isGranted({ identityRole, permission }).hasPermission
  );
}

/**
 * Whether an identity's role is allowed to grant every API key scope,
 * i.e. create a full-access ("*") personal API key.
 */
export function canGrantFullAccess(identityRole: IdentityRole): boolean {
  return getGrantableApiKeyScopes(identityRole).length === allApiKeyScopeCount;
}
